import React, { useEffect, useState } from "react";
import { MainContainer, Section } from "./styles";
import Typography from "../../../../components/Typography";
import SectionItem from "../../../../components/SectionItem";
import { Divider } from "antd";
import useEnterprise from "../../hooks/useEnterprise";
import { Persona as PersonaType } from "./types";
import { personaService } from "../../../../services/modules/persona";
import WaitingMessage from "../../../../components/WaitingMessage";
import { SkeletonLoading } from "../skeleton";

const Persona: React.FC = (): JSX.Element => {
  const { enterpriseState } = useEnterprise();

  const [personaData, setPersonaData] = useState<PersonaType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchPersona = (id: string) => {
    personaService
      .getPersona(id)
      .then((response) => setPersonaData(response))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (enterpriseState.selectedEnterprise)
      fetchPersona(enterpriseState.selectedEnterprise);
  }, [enterpriseState.selectedEnterprise]);

  return (
    <Section>
      {isLoading ? (
        <SkeletonLoading />
      ) : (
        <>
          {personaData ? (
            <MainContainer>
              <Typography
                text={personaData.profile.name}
                size="extraHuge"
                color="Black500"
                weight={700}
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"PERFIL"}
                description={
                  "Contexto e características gerais da persona, como o ambiente em que vive, sua personalidade e um resumo biográfico."
                }
                isTitle
              />
              <SectionItem
                icon={"🚻"}
                title={"Gênero"}
                description={personaData.profile.gender}
              />
              <SectionItem
                icon={"🔢"}
                title={"Idade"}
                description={personaData.profile.age}
              />
              <SectionItem
                icon={"💍"}
                title={"Estado Civil"}
                description={personaData.profile.marital_status}
              />
              <SectionItem
                icon={"🧑‍🚀"}
                title={"Profissão"}
                description={personaData.profile.profession}
              />
              <SectionItem
                icon={"💵"}
                title={"Renda Mensal"}
                description={personaData.profile.monthly_income}
              />
              <SectionItem
                icon={"🗺"}
                title={"Localização"}
                description={personaData.profile.location}
              />
              <SectionItem
                icon={"🎓"}
                title={"Educação"}
                description={personaData.profile.education}
              />
              {personaData.profile.hobbies.map((hobby, index) => (
                <SectionItem
                  key={index}
                  icon={"💜"}
                  title={`Hobby ${index + 1}`}
                  description={hobby}
                />
              ))}
              <SectionItem
                icon={"🤝"}
                title={"Habilidade Interpessoal"}
                description={personaData.profile.interpersonal_skill}
              />
              <SectionItem
                icon={"🔩"}
                title={"Habilidade Técnica"}
                description={personaData.profile.technical_skill}
              />
              <SectionItem
                icon={"📱"}
                title={"Redes Sociais"}
                description={personaData.profile.social_networks.join(", ")}
              />
              <SectionItem
                icon={"🏆"}
                title={"Marcas Preferidas"}
                description={personaData.profile.favorite_brands.join(", ")}
              />
              <SectionItem
                icon={"📜"}
                title={"Bio"}
                description={personaData.profile.bio}
              />
              <SectionItem
                icon={"🌟"}
                title={"Personalidade"}
                description={personaData.profile.personality}
              />
              <SectionItem
                icon={"⏰"}
                title={"Rotina Diária"}
                description={personaData.profile.daily_routine}
              />
              <SectionItem
                icon={"🏡"}
                title={"Vizinhança"}
                description={personaData.profile.neighborhood}
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"PASSADO E PRESENTE"}
                description={
                  "Informações sobre a história pessoal da persona, incluindo experiências anteriores e sua jornada profissional até o momento."
                }
                isTitle
              />
              <SectionItem
                icon={"📖"}
                title={"História Pessoal"}
                description={personaData.past_and_future.personal_history}
              />
              <SectionItem
                icon={"🚀"}
                title={"Jornada Profissional"}
                description={personaData.past_and_future.professional_journey}
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"FATORES INTERNOS"}
                description={
                  "Aspectos internos da persona, como suas motivações, medos e influências pessoais, que afetam suas decisões e comportamento."
                }
                isTitle
              />
              <SectionItem
                icon={"🌱"}
                title={"Inspirações"}
                description={personaData.internal_factors.inspirations}
              />
              <SectionItem
                icon={"🔥"}
                title={"Motivações"}
                description={personaData.internal_factors.motivations}
              />
              <SectionItem
                icon={"🎯"}
                title={"Objetivos Pessoais"}
                description={personaData.internal_factors.personal_goals}
              />
              <SectionItem
                icon={"🥇"}
                title={"Sucesso Pessoal"}
                description={personaData.internal_factors.personal_success}
              />
              <SectionItem
                icon={"😤"}
                title={"Desafios e Frustrações"}
                description={
                  personaData.internal_factors
                    .personal_challenges_and_frustrations
                }
              />
              <SectionItem
                icon={"💭"}
                title={"Sonhos"}
                description={personaData.internal_factors.dreams}
              />
              <SectionItem
                icon={"😨"}
                title={"Medos"}
                description={personaData.internal_factors.fears}
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"VIDA PROFISSIONAL"}
                description={
                  "Detalhes sobre a rotina diária, desafios enfrentados no trabalho, objetivos prossionais e sucessos alcançados pela persona."
                }
                isTitle
              />
              <SectionItem
                icon={"🎯"}
                title={"Objetivos Profissionais"}
                description={personaData.professional_life.professional_goals}
              />
              <SectionItem
                icon={"🏆"}
                title={"Sucesso Profissional"}
                description={personaData.professional_life.professional_success}
              />
              <SectionItem
                icon={"🤺"}
                title={"Desafios Profissionais"}
                description={
                  personaData.professional_life.professional_challenges
                }
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"DESCOBERTA E JORNADA"}
                description={
                  "Processo de descoberta de produtos e experiências de compra da persona, bem como a jornada que ela percorre para tomar decisões."
                }
                isTitle
              />
              <SectionItem
                icon={"🔍"}
                title={"Descoberta de Produtos"}
                description={
                  personaData.discovery_and_journey.new_product_discovery
                }
              />
              <SectionItem
                icon={"🛒"}
                title={"Experiência de Compra Comum"}
                description={
                  personaData.discovery_and_journey.common_shopping_experience
                }
              />
              <SectionItem
                icon={"😊"}
                title={"Experiência Positiva de Compra"}
                description={
                  personaData.discovery_and_journey.positive_shopping_experience
                }
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"INFLUÊNCIAS EXTERNAS"}
                description={
                  "Fatores externos que inuenciam as decisões da persona, como sugestões de amigos, familiares e outras pessoas em seu círculo social."
                }
                isTitle
              />
              <SectionItem
                icon={"🤔"}
                title={"Como decide comprar"}
                description={personaData.external_influences.how_decides_to_buy}
              />
              <SectionItem
                icon={"🌟"}
                title={"Características Valorizadas"}
                description={personaData.external_influences.valued_features}
              />
              <SectionItem
                icon={"👯"}
                title={"Outros influenciadores"}
                description={personaData.external_influences.other_influences}
              />
              <Divider />
              <SectionItem
                icon={"👤"}
                title={"DORES E CONCORRÊNCIA"}
                description={
                  "Dores, problemas especícos e concorrência que afetam a persona, além das soluções atuais que ela utiliza"
                }
                isTitle
              />
              <SectionItem
                icon={"🔴"}
                title={"Problemas Específicos"}
                description={personaData.pain_and_competition.specific_problems}
              />
              <SectionItem
                icon={"🔧"}
                title={"Soluções Atuais"}
                description={
                  personaData.pain_and_competition.current_solutions_used
                }
              />
              <SectionItem
                icon={"👍"}
                title={"Pontos Positivos"}
                description={
                  personaData.pain_and_competition.likes_in_current_solutions
                }
              />
              <SectionItem
                icon={"👎"}
                title={"Desvantagens"}
                description={
                  personaData.pain_and_competition.unlikes_in_current_solutions
                }
              />
            </MainContainer>
          ) : (
            <WaitingMessage />
          )}
        </>
      )}
    </Section>
  );
};

export default Persona;
